import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { Sparkline } from "@/components/data/Sparkline";
import { BasisBadge } from "@/components/trust/BasisBadge";
import { ConfidenceIndicator } from "@/components/trust/ConfidenceIndicator";

const CHAIN_FILTERS = [
  { key: "all", label: "All" },
  { key: "benzene", label: "Benzene" },
  { key: "toluene", label: "Toluene" },
  { key: "valorization", label: "Valorization" },
  { key: "shared", label: "Shared" },
];

const COLUMNS = [
  { key: "tag_id", label: "Tag ID" },
  { key: "element_name", label: "Element" },
  { key: "chain", label: "Chain" },
  { key: "value", label: "Value" },
  { key: "trend", label: "Trend" },
  { key: "basis", label: "Basis" },
  { key: "confidence", label: "Confidence" },
];

export default function Tags() {
  const { data, error } = usePolling(() => api.get("/tags/current").then((r) => r.data), 8000);
  const [search, setSearch] = useState("");
  const [chainFilter, setChainFilter] = useState("all");
  const [sortKey, setSortKey] = useState("tag_id");
  const [sortAsc, setSortAsc] = useState(true);
  const navigate = useNavigate();

  const rows = useMemo(() => {
    if (!data) return [];
    let filtered = data.filter((t) => chainFilter === "all" || t.chain === chainFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter((t) => t.tag_id.toLowerCase().includes(q) || t.name.toLowerCase().includes(q) || t.element_name.toLowerCase().includes(q));
    }
    filtered = [...filtered].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === "number" && typeof bv === "number") return sortAsc ? av - bv : bv - av;
      return sortAsc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return filtered;
  }, [data, search, chainFilter, sortKey, sortAsc]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortAsc((a) => !a);
    else { setSortKey(key); setSortAsc(true); }
  };

  if (error) return <div className="text-sm text-[var(--oim-text-muted)]" data-testid="tags-error">Could not load tag list.</div>;
  if (!data) return <div className="text-sm text-[var(--oim-text-muted)]" data-testid="tags-loading">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="tags-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-heading text-[var(--oim-text)]">Tag List</h1>
          <div className="text-xs text-[var(--oim-text-dim)] mt-1">{rows.length} of {data.length} raw tags — Simulated Demo Data</div>
        </div>
        <div className="flex items-center gap-2 rounded-md border px-3 py-1.5" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}>
          <Search className="w-3.5 h-3.5 text-[var(--oim-text-dim)]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tag, name or element…"
            className="bg-transparent text-xs outline-none text-[var(--oim-text)] placeholder:text-[var(--oim-text-dim)] w-56"
            data-testid="tags-search-input"
          />
        </div>
      </div>

      <div className="flex items-center gap-1.5" data-testid="tags-chain-filter">
        {CHAIN_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setChainFilter(f.key)}
            data-testid={`tags-chain-filter-${f.key}`}
            className="text-[10px] font-heading uppercase tracking-wide px-2.5 py-1 rounded-full border transition-colors"
            style={{
              borderColor: "var(--oim-border)",
              backgroundColor: chainFilter === f.key ? "var(--oim-surface-softer)" : "transparent",
              color: chainFilter === f.key ? "var(--oim-text)" : "var(--oim-text-muted)",
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}>
        <div className="grid grid-cols-[1.6fr_1.4fr_0.8fr_1fr_1fr_0.9fr_1fr] gap-2 px-4 py-2 border-b" style={{ borderColor: "var(--oim-border)" }}>
          {COLUMNS.map((c) => (
            <button
              key={c.key}
              onClick={() => c.key !== "trend" && toggleSort(c.key)}
              className="text-left text-[9px] font-heading uppercase tracking-wide text-[var(--oim-text-dim)] hover:text-[var(--oim-text-muted)]"
              data-testid={`tags-sort-${c.key}`}
            >
              {c.label}{sortKey === c.key ? (sortAsc ? " ▲" : " ▼") : ""}
            </button>
          ))}
        </div>
        {rows.length === 0 && <div className="px-4 py-6 text-xs text-[var(--oim-text-dim)]" data-testid="tags-empty">No tags match your search.</div>}
        {rows.map((t) => (
          <button
            key={t.tag_id}
            onClick={() => navigate(`/tags/${t.tag_id}`)}
            className="grid grid-cols-[1.6fr_1.4fr_0.8fr_1fr_1fr_0.9fr_1fr] gap-2 px-4 py-2.5 border-b w-full text-left hover:bg-[var(--oim-hover)] transition-colors"
            style={{ borderColor: "var(--oim-border)" }}
            data-testid={`tags-row-${t.tag_id}`}
          >
            <span className="font-mono-data text-xs text-[var(--oim-text)] truncate">{t.tag_id}</span>
            <span className="text-xs text-[var(--oim-text-secondary)] truncate">{t.element_name}</span>
            <span className="text-[9px] uppercase tracking-wide text-[var(--oim-text-dim)] self-center">{t.chain}</span>
            <span className="font-mono-data text-xs font-semibold text-[var(--oim-text)] self-center">
              {t.basis === "unobservable" ? <span className="text-[var(--oim-text-dim)]">— not directly observable</span> : <>{t.value} <span className="text-[var(--oim-text-dim)]">{t.unit}</span></>}
            </span>
            <span className="self-center"><Sparkline values={t.sparkline} /></span>
            <span className="self-center"><BasisBadge basis={t.basis} /></span>
            <span className="self-center"><ConfidenceIndicator level={t.confidence} /></span>
          </button>
        ))}
      </div>
    </div>
  );
}
